import React, { useState, useEffect } from "react";
import { GraphQLResult } from "@aws-amplify/api";
import { API, graphqlOperation } from "aws-amplify";
import { createAppConfig, updateAppConfig } from "../graphql/mutations";
import { getAppConfig } from "../graphql/queries";
import { Button, CheckboxField, TextField } from "@aws-amplify/ui-react";
import { Container } from "react-bootstrap";
import { Notification } from "./Notification/Notification";
import { Modal } from "./Modal/Modal";

type AppConfig = {
  id: string;
  minimumAppVersion: string;
  disableTI: boolean;
  disableTMC: boolean;
  disableApp: boolean;
  disabledMessageTI: string;
  disabledMessageTMC: string;
  disabledMessageApp: string;
};

type appConfigResultType = {
  getAppConfig: AppConfig[];
};

export default function FeatureFlags() {
  const [appConfigItem, setAppConfigItem] = useState<AppConfig>({
    id: "0",
    minimumAppVersion: "",
    disableTI: false,
    disableTMC: false,
    disableApp: false,
    disabledMessageTI: "",
    disabledMessageTMC: "",
    disabledMessageApp: "",
  });
  const [loaded, setLoaded] = useState<boolean>(false);
  const [disableTI, setDisableTI] = useState<boolean>(false);
  const [disableTMC, setDisableTMC] = useState<boolean>(false);
  const [disableApp, setDisableApp] = useState<boolean>(false);
  const [disabledMessageTI, setDisabledMessageTI] = useState<string>("");
  const [disabledMessageTMC, setDisabledMessageTMC] = useState<string>("");
  const [disabledMessageApp, setDisabledMessageApp] = useState<string>("");
  const [minimumAppVersion, setMinimumAppVersion] = useState<string>("");
  const [useUpdateFunction, setUseUpdateFunction] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false as boolean);
  const [toastTitle, setToastTitle] = useState("" as string);
  const [toastDescription, setToastDescription] = useState("" as string);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchAppConfig();
    setLoaded(true);
  }, []);

  const fetchAppConfig = async () => {
    try {
      const result = (await API.graphql(
        graphqlOperation(getAppConfig)
      )) as GraphQLResult<appConfigResultType>;
      const items = result?.data?.getAppConfig;
      console.log(items);
      if (items.length === 0) {
        setUseUpdateFunction(false);
      } else {
        setUseUpdateFunction(true);
      }
      setAppConfigItem(items[0]);
      setDisableTI(items[0].disableTI);
      setDisableTMC(items[0].disableTMC);
      setDisableApp(items[0].disableApp);
      setDisabledMessageTI(items[0].disabledMessageTI);
      setDisabledMessageTMC(items[0].disabledMessageTMC);
      setDisabledMessageApp(items[0].disabledMessageApp);
      setMinimumAppVersion(items[0].minimumAppVersion);
    } catch (e) {
      console.log(e);
    }
  };

  const createAppConfigFunc = async () => {
    try {
      await API.graphql(
        graphqlOperation(createAppConfig, {
          appConfig: {
            id: "0",
            disableTI: disableTI,
            disableTMC: disableTMC,
            disableApp: disableApp,
            disabledMessageTI: disabledMessageTI,
            disabledMessageTMC: disabledMessageTMC,
            disabledMessageApp: disabledMessageApp,
            minimumAppVersion: minimumAppVersion,
          },
        })
      );
      fetchAppConfig();
      return "success";
    } catch (e) {
      console.log("error creating app config");
    }
  };

  const updateAppConfigFunc = async () => {
    try {
      await API.graphql(
        graphqlOperation(updateAppConfig, {
          appConfig: {
            id: "0",
            disableTI: disableTI,
            disableTMC: disableTMC,
            disableApp: disableApp,
            disabledMessageTI: disabledMessageTI,
            disabledMessageTMC: disabledMessageTMC,
            disabledMessageApp: disabledMessageApp,
            minimumAppVersion: minimumAppVersion,
          },
        })
      );
      fetchAppConfig();
      return "success";
    } catch (e) {
      console.log("error updating app config");
    }
  };

  const handleCreateAppConfig = async () => {
    const result = await createAppConfigFunc();
    if (result === "success") {
      setToastTitle("Success!");
      setToastDescription("You successfully created the application config");
      setShowToast(true);
    } else {
      setToastTitle("Error!");
      setToastDescription("App config failed to create");
      setShowToast(true);
    }
    setShowCreateModal(false);
  };

  const handleUpdateAppConfig = async () => {
    const result = await updateAppConfigFunc();
    if (result === "success") {
      setToastTitle("Success!");
      setToastDescription("You successfully updated the application config");
      setShowToast(true);
    } else {
      setToastTitle("Error!");
      setToastDescription("App config failed to update");
      setShowToast(true);
    }
    setShowUpdateModal(false);
  };

  const handleNotificationClose = () => {
    setShowToast(false);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  return (
    <Container style={{ padding: 25 }}>
      <h2>App Config</h2>
      {loaded && (
        <div>
          <div style={{ marginTop: 20 }}>
            <CheckboxField
              name="subscribe-controlled"
              value="yes"
              checked={disableTI}
              onChange={(e) => setDisableTI(e.target.checked)}
              label="Disable Travel Insurance functionallity in app (checked = disabled)"
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <TextField
              autoComplete="off"
              descriptiveText=""
              direction="column"
              hasError={false}
              inputMode="text"
              isDisabled={false}
              isReadOnly={false}
              isRequired={true}
              label="Message to show for disabled TI"
              labelHidden={false}
              name="last_name"
              placeholder=""
              type="text"
              wrap="nowrap"
              value={disabledMessageTI}
              onChange={(e: any) => setDisabledMessageTI(e.currentTarget.value)}
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <CheckboxField
              name="subscribe-controlled"
              value="yes"
              checked={disableTMC}
              onChange={(e) => setDisableTMC(e.target.checked)}
              label="Disable Travel money card functionallity in app (checked = disabled)"
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <TextField
              autoComplete="off"
              descriptiveText=""
              direction="column"
              hasError={false}
              inputMode="text"
              isDisabled={false}
              isReadOnly={false}
              isRequired={true}
              label="Message to show for disabled TMC"
              labelHidden={false}
              name="last_name"
              placeholder=""
              type="text"
              wrap="nowrap"
              value={disabledMessageTMC}
              onChange={(e: any) =>
                setDisabledMessageTMC(e.currentTarget.value)
              }
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <CheckboxField
              name="subscribe-controlled"
              value="yes"
              checked={disableApp}
              onChange={(e) => setDisableApp(e.target.checked)}
              label="Disable all app functionallity (user cannot proceed past passcode page)"
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <TextField
              autoComplete="off"
              descriptiveText=""
              direction="column"
              hasError={false}
              inputMode="text"
              isDisabled={false}
              isReadOnly={false}
              isRequired={true}
              label="Message to show for disabled App"
              labelHidden={false}
              name="last_name"
              placeholder=""
              type="text"
              wrap="nowrap"
              value={disabledMessageApp}
              onChange={(e: any) =>
                setDisabledMessageApp(e.currentTarget.value)
              }
            />
          </div>

          <div style={{ marginTop: 20 }}>
            <TextField
              autoComplete="off"
              descriptiveText=""
              direction="column"
              hasError={false}
              inputMode="text"
              isDisabled={false}
              isReadOnly={false}
              isRequired={true}
              label="Minimum App Version"
              labelHidden={false}
              name="last_name"
              placeholder=""
              type="number"
              wrap="nowrap"
              value={minimumAppVersion}
              onChange={(e: any) => setMinimumAppVersion(e.currentTarget.value)}
            />
          </div>
        </div>
      )}
      {useUpdateFunction ? (
        <Button
          onClick={() => setShowUpdateModal(true)}
          style={{ marginTop: 40 }}
        >
          Update App Config
        </Button>
      ) : (
        <Button
          onClick={() => setShowCreateModal(true)}
          style={{ marginTop: 40 }}
        >
          Create App Config
        </Button>
      )}
      <div className="notificationContainer">
        <Notification
          show={showToast}
          timer={4000}
          position="bottom-end"
          title={toastTitle}
          description={toastDescription}
          autohide={true}
          handleClose={handleNotificationClose}
        />
      </div>
      <Modal
        enableModal={showUpdateModal}
        handleClose={handleCloseUpdateModal}
        handleSubmit={() => handleUpdateAppConfig()}
        title={"WARNING"}
        closeButtonText="Back"
        actionButtonText="Update"
        width="halfwidth"
      >
        Are you sure you want to update the application configuration? Disabling
        app functionality should only be done if absolutely neccessary.
      </Modal>
      <Modal
        enableModal={showCreateModal}
        handleClose={handleCloseCreateModal}
        handleSubmit={() => handleCreateAppConfig()}
        title={"WARNING"}
        closeButtonText="Back"
        actionButtonText="Create"
        width="halfwidth"
      >
        Double check the application configuration is correct before proceeding
        to create.
      </Modal>
    </Container>
  );
}
